import React, { useCallback, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import urls from "../../../../global/constants/UrlConstants";
import PersonalDetails from "../stepper copy/Personal Details/PersonalDetails";
import OfficialInformation from "../stepper copy/OfficialInformation/OfficialInformation";
import EmployeeDocuments from "../stepper copy/EmployeeDocuments/EmployeeDocuments";
import YhButton from "../../../../ui/YhButton";

const stepData = ["Lead Details", "Upload File", "Field Mapping"];

export type StepActions = {
  submit: () => void | Promise<void>;
};

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseAddTeam?: Function;
  userId?: string;
  setUserId?: Function;
  fromSettingOrLogout?: string;
  addNewTeamDialog?: any;
  eventForEdit?: any;
}

const ExportNewTeam = (props: CustomProps = {}) => {
  const history = useHistory();
  const isEmbedded = props.isDialogOpen !== undefined;
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState(props.userId || "");
  const stepActionsRef = useRef<StepActions | null>(null);

  const registerStepActions = useCallback((actions: StepActions) => {
    stepActionsRef.current = actions;
  }, []);

  const handleClose = () => {
    if (props.handleCloseAddTeam) {
      props.handleCloseAddTeam();
      return;
    }
    history.push(urls.bulkUploade);
  };

  const nextStep = () => {
    setActiveStep((s) => Math.min(s + 1, stepData.length - 1));
  };

  const backStep = () => {
    if (activeStep === 0) {
      handleClose();
      return;
    }
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleFooterSave = async () => {
    if (!stepActionsRef.current?.submit) return;
    try {
      setSaving(true);
      await stepActionsRef.current.submit();
    } finally {
      setSaving(false);
    }
  };

  const footerLabel =
    activeStep === 0
      ? "Save & Continue"
      : activeStep === 1
      ? "Save & Continue"
      : "Submit";

  const formBody = (
    <div
      className={`relative flex flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0 ${
        isEmbedded
          ? "h-[min(90vh,880px)] w-full max-w-5xl"
          : "h-[calc(100vh-68px)] max-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
            Import Leads
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Select a form, upload a CSV, and map fields to import leads.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="mb-4 flex shrink-0 gap-2 overflow-x-auto">
        {stepData.map((label, index) => {
          const isActive = index === activeStep;
          const isDone = index < activeStep;
          return (
            <button
              key={label}
              type="button"
              onClick={() => {
                if (index <= activeStep) setActiveStep(index);
              }}
              className={`inline-flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
                isActive
                  ? "border-[#A02448] bg-[#EEF0FF] text-[#4338CA]"
                  : isDone
                  ? "border-[#D1D5DB] bg-white text-[#374151]"
                  : "border-[#E5E7EB] bg-white text-[#9CA3AF]"
              }`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                  isActive || isDone
                    ? "bg-[#A02448] text-white"
                    : "bg-[#F3F4F6] text-[#9CA3AF]"
                }`}
              >
                {index + 1}
              </span>
              {label}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden pb-20">
        <section className="h-full min-h-0 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
          {activeStep === 0 && (
            <PersonalDetails
              userId={userId}
              handleCloseAddTeam={handleClose}
              nextStep={nextStep}
              setUserId={(v: string) => {
                setUserId(v);
                props.setUserId?.(v);
              }}
              addNewTeamDialog={props.addNewTeamDialog || { open: false, edit: false }}
              fromSettingOrLogout={props.fromSettingOrLogout}
              eventForEdit={props.eventForEdit}
              registerStepActions={registerStepActions}
              hideFooterButtons
            />
          )}
          {activeStep === 1 && (
            <OfficialInformation
              userId={userId}
              nextStep={nextStep}
              backStep={backStep}
              fromSettingOrLogout={props.fromSettingOrLogout}
              registerStepActions={registerStepActions}
              hideFooterButtons
            />
          )}
          {activeStep === 2 && (
            <EmployeeDocuments
              userId={userId}
              nextStep={nextStep}
              handleCloseAddTeam={handleClose}
              backStep={backStep}
              fromSettingOrLogout={props.fromSettingOrLogout}
              registerStepActions={registerStepActions}
              hideFooterButtons
            />
          )}
        </section>
      </div>

      <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
        <button
          type="button"
          onClick={backStep}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <YhButton variant="secondary" size="sm" onClick={backStep}>
            Back
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            loading={saving}
            onClick={handleFooterSave}
          >
            {footerLabel}
          </YhButton>
        </div>
      </div>
    </div>
  );

  if (isEmbedded) {
    if (!props.isDialogOpen) return null;
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-5xl overflow-hidden rounded-2xl shadow-xl">
          {formBody}
        </div>
      </div>
    );
  }

  return formBody;
};

export default ExportNewTeam;
